import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, Truck, MapPin } from "lucide-react";
import { http, formatPrice } from "@/lib/api";

const OrderConfirmation = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    http
      .get(`/orders/${id}`)
      .then((r) => setOrder(r.data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="py-24 text-center text-[#878787]">Loading…</div>;
  if (!order) return <div className="py-24 text-center text-[#878787]">Order not found.</div>;

  return (
    <main className="max-w-[900px] mx-auto px-2 sm:px-4 py-6 fk-fade-up" data-testid="order-confirmation">
      <div className="bg-white rounded-sm fk-shadow p-6 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#388e3c] mx-auto" data-testid="order-success-icon" />
        <h1 className="font-head text-2xl font-bold text-[#212121] mt-3">Order Confirmed!</h1>
        <p className="text-sm text-[#555] mt-1">
          Thank you. Your order has been placed successfully.
        </p>
        <div className="inline-flex items-center gap-2 mt-4 bg-[#f1f3f6] rounded-sm px-4 py-2 text-sm">
          <Package className="w-4 h-4 text-[#C62828]" />
          Order ID: <b data-testid="order-id">{order.id}</b>
        </div>
        <div className="flex items-center justify-center gap-2 mt-3 text-sm text-[#388e3c] font-medium" data-testid="order-status">
          <Truck className="w-4 h-4" /> {order.status} · Arriving by {order.est_delivery}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
        <div className="bg-white rounded-sm fk-shadow p-4">
          <p className="font-head font-bold text-[#212121] mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#C62828]" /> Delivery Address
          </p>
          <p className="text-sm text-[#333] leading-relaxed">
            <b>{order.address.name}</b> · +91 {order.address.phone}
            <br />
            {[order.address.address, order.address.locality].filter(Boolean).join(", ")}
            <br />
            {order.address.city}, {order.address.state} - {order.address.pincode}
          </p>
          <p className="text-xs text-[#878787] mt-3" data-testid="order-payment-info">
            {order.payment_method === "cod"
              ? `Cash on Delivery — pay ${formatPrice(order.payable)} on delivery · Phone verified via SMS OTP`
              : `Payment: ${order.payment_method} (${order.payment_status})`}
          </p>
        </div>

        <div className="bg-white rounded-sm fk-shadow p-4">
          <p className="font-head font-bold text-[#212121] mb-3">Price Summary</p>
          <div className="space-y-2 text-sm text-[#333]">
            <div className="flex justify-between"><span>Total MRP</span><span>{formatPrice(order.total_mrp)}</span></div>
            <div className="flex justify-between"><span>Discount</span><span className="text-[#388e3c]">− {formatPrice(order.discount)}</span></div>
            <div className="flex justify-between"><span>Delivery</span><span>{order.delivery === 0 ? "FREE" : formatPrice(order.delivery)}</span></div>
            {order.cod_fee > 0 && <div className="flex justify-between" data-testid="order-cod-fee"><span>COD Fee</span><span>{formatPrice(order.cod_fee)}</span></div>}
            <div className="flex justify-between font-semibold text-base border-t border-dashed pt-2"><span>{order.payment_method === "cod" ? "Pay on Delivery" : "Paid"}</span><span>{formatPrice(order.payable)}</span></div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-sm fk-shadow mt-4">
        <p className="font-head font-bold text-[#212121] px-4 py-3 border-b border-[#f0f0f0]">
          Items ({order.items.length})
        </p>
        {order.items.map((it) => (
          <div key={it.id} className="flex gap-4 p-4 border-b border-[#f0f0f0] last:border-0">
            <div className="w-16 h-16 flex items-center justify-center shrink-0">
              <img src={it.image_url} alt={it.name} className="max-h-full max-w-full object-contain mix-blend-multiply" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-[#212121] line-clamp-2">{it.name}</p>
              <p className="text-xs text-[#878787] mt-1">Qty: {it.qty}</p>
            </div>
            <span className="font-semibold text-sm">{formatPrice(it.price * it.qty)}</span>
          </div>
        ))}
      </div>

      <div className="text-center mt-5">
        <Link
          to="/"
          className="inline-block bg-[#C62828] text-white font-semibold px-10 py-2.5 rounded-sm hover:bg-[#A01F1F]"
          data-testid="continue-shopping-btn"
        >
          Continue Shopping
        </Link>
      </div>
    </main>
  );
};

export default OrderConfirmation;
